'use client'

import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { debounce } from 'lodash'
import {
  autocompleteOpenSearch,
  autocompleteLocationOpenSearch,
} from '@/src/api'
import { AutoCompleteProps } from 'antd'

type ApiResponse = {
  spellSuggestion?: string
  body: Array<{
    full_name?: string
    full_name_display?: string
    sub_category?: string
    category?: string
    category_display?: string
    category_value?: string
    group?: string
  }>
}

/**
 * Custom hook to fetch autocomplete suggestions from the API.
 * @param {string} currentSearchBarCategory - The current category of the search bar.
 */
export const useAutocomplete = (currentSearchBarCategory: string) => {
  const [autocompleteQuery, setAutocompleteQuery] = useState<{
    key: string
    value: string
  }>({
    key: currentSearchBarCategory,
    value: '',
  })
  const [options, setOptions] = useState<AutoCompleteProps['options']>([])
  const [spellSuggestion, setSpellSuggestion] = useState<string>('')
  const fetchedAutocompleteQueryRef = useRef<string>('')

  /**
   * Processes the API response and updates the autocomplete options.
   * @param {ApiResponse} response - The API response data.
   * @param {string} key - The key of the autocomplete query.
   */
  const processApiResponse = (response: ApiResponse, key: string) => {
    if (!response?.body?.length) {
      setOptions([])
      setSpellSuggestion(response?.spellSuggestion || '')
      return
    }

    if (key === 'full_name') {
      setOptions(
        response.body.map((item, idx) => ({
          label: item.full_name,
          value: item.full_name,
          key: `full-name-${idx}`,
          highlightedName: item.full_name_display,
        }))
      )
      setSpellSuggestion(response?.spellSuggestion || '')
      return
    }

    const specialtyItems: any[] = []
    const conditionItems: any[] = []
    const seenCategories = new Set<string>()

    response.body.forEach((item, index) => {
      if (!item) return

      const categoryIdentifier = `${item.category}-${item.sub_category}`
      if (seenCategories.has(categoryIdentifier)) return
      seenCategories.add(categoryIdentifier)

      const option = {
        label: item.category,
        value: item.category_value,
        key: `${item.group}-${index}`,
        category: item.category_value,
        sub_category: item.sub_category,
        group: item.group || 'unknown',
        highlightedCategory: item.category_display,
      }

      if (item.group === 'specialty') specialtyItems.push(option)
      else if (item.group === 'disease_state') conditionItems.push(option)
    })

    const groupedOptions = []

    if (conditionItems.length)
      groupedOptions.push({
        label: 'Conditions',
        options: conditionItems,
        key: 'conditions-group',
      })
    if (specialtyItems.length)
      groupedOptions.push({
        label: 'Specialties',
        options: specialtyItems,
        key: 'specialties-group',
      })

    setOptions(groupedOptions)
    setSpellSuggestion(response?.spellSuggestion || '')
    return groupedOptions
  }

  const debouncedAutocomplete = useMemo(
    () =>
      debounce(async (key: string, value: string) => {
        if (!value?.trim() || value.length < 2) {
          setOptions([])
          setSpellSuggestion('')
          return
        }

        try {
          const response: ApiResponse = await autocompleteOpenSearch({
            key,
            value,
          })
          fetchedAutocompleteQueryRef.current = value
          processApiResponse(response, key)
        } catch (error) {
          console.error('Error during autocomplete:', error)
          setOptions([])
          setSpellSuggestion('')
        }
      }, 300),
    [autocompleteQuery]
  )

  useEffect(() => {
    const { key, value } = autocompleteQuery
    debouncedAutocomplete(key, value)

    return () => {
      debouncedAutocomplete.cancel()
    }
  }, [autocompleteQuery, debouncedAutocomplete])

  const fetchCategoryAutoSelection = useCallback(
    async (partialSearchCategory: string) => {
      debouncedAutocomplete.cancel()
      const fetchedAutocompleteQuery = fetchedAutocompleteQueryRef.current
      let newOptions = options || []

      if (partialSearchCategory !== fetchedAutocompleteQuery) {
        const { key, value } = autocompleteQuery

        if (!value?.trim() || value.length < 2) {
          newOptions = []
        } else {
          const response: ApiResponse = await autocompleteOpenSearch({
            key,
            value,
          })
          const groupedOptions = processApiResponse(response, key)
          if (groupedOptions?.length) {
            newOptions = groupedOptions
          }
        }
      }

      if (newOptions?.length) {
        const firstOption = newOptions[0].options[0]
        const {
          highlightedCategory,
          highlightedSubCategory,
          category,
          sub_category,
          group,
        } = firstOption
        return {
          key: group,
          value: highlightedCategory
            ? category
            : highlightedSubCategory
            ? sub_category
            : '',
        }
      }

      return { key: 'unified', value: '' }
    },
    [autocompleteQuery, debouncedAutocomplete, options]
  )

  return {
    autocompleteQuery,
    setAutocompleteQuery,
    options,
    spellSuggestion,
    setSpellSuggestion,
    fetchCategoryAutoSelection,
  }
}

/**
 * Hook to handle location autocomplete suggestions.
 */
export const useLocationAutocomplete = () => {
  const [locationAutocompleteQuery, setLocationAutocompleteQuery] = useState('')
  const [locationOptions, setLocationOptions] = useState<
    AutoCompleteProps['options']
  >([])

  const debouncedLocationOptions = useMemo(
    () =>
      debounce(async (query: string) => {
        if (!query?.length || query.length < 2) {
          setLocationOptions([])
          return
        }

        try {
          const response = await autocompleteLocationOpenSearch(query)
          const data = response.body.map((item: any) => {
            if (!item) return {}

            return {
              value: item.key,
              label: item.key,
              key: `location-${item.key}`,
              highlightedName: item.location_display,
            }
          })
          setLocationOptions(data)
        } catch (error) {
          console.error('Error fetching location options:', error)
          setLocationOptions([])
        }
      }, 300),
    []
  )

  useEffect(() => {
    debouncedLocationOptions(locationAutocompleteQuery)
    return () => debouncedLocationOptions.cancel()
  }, [locationAutocompleteQuery, debouncedLocationOptions])

  return {
    locationAutocompleteQuery,
    setLocationAutocompleteQuery,
    locationOptions,
  }
}

/**
 * Hook to combine autocomplete and location autocomplete hooks.
 * @param {string} currentSearchBarCategory - The current search bar category.
 */
export const useApi = (currentSearchBarCategory: string) => {
  const {
    autocompleteQuery,
    setAutocompleteQuery,
    options,
    spellSuggestion,
    setSpellSuggestion,
    fetchCategoryAutoSelection,
  } = useAutocomplete(currentSearchBarCategory)
  const {
    locationAutocompleteQuery,
    setLocationAutocompleteQuery,
    locationOptions,
  } = useLocationAutocomplete()

  return {
    autocompleteQuery,
    setAutocompleteQuery,
    locationAutocompleteQuery,
    setLocationAutocompleteQuery,
    options,
    spellSuggestion,
    setSpellSuggestion,
    locationOptions,
    fetchCategoryAutoSelection,
  }
}
